import { Query } from '@apollo/client/react/components';
import { AccountClient } from '@nile/graphql';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/banyan/src/Pebbles/select';
import { DropdownArrow } from '@nile/mangifera/src/Pebbles/select';
import {
  reactSelectInlineError,
  reactSelectErrorStyles,
  SelectContainer,
  Icon,
  getTranslatedText,
  BackgroundIcon,
  useSiteListForDropdown,
  useProduct
} from '@nile/components';
import { Badge, Spinner } from '@superops/ui-design-system';
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import envConfig from '@nile/home/src/Config/env';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { isEmpty } from 'lodash';

const SelectWrapper = styled.div`
  .container > div {
    border: none !important;
  }

  .container {
    margin: ${(props) =>
      props?.noMargin
        ? '0px'
        : props?.fullWidth
        ? '0px 0px 0px 10px'
        : '0px 10px'};
    width: 100%;
  }
`;

const ReadMode = styled.div`
  font-weight: bold;
  &:hover {
    svg {
      visibility: visible;
    }
  }
  svg {
    visibility: hidden;
  }
`;

const SiteSelect = (props) => {
  const {
    intl,
    errors,
    icon,
    selectAllSitesOption,
    hideLabel,
    hideIcon,
    ignoreAutoPopulateFirstValue = false,
    mode = 'edit',
    defaultMenuOpen,
    roundedIcon = false,
    prePopulateOptions = null,
    disabled,
    disableApiCalls = false,
    menuStyleProps = {},
    menuListStyleProps = {},
    noMargin = false,
    multiSelect = false,
    disableClearable = false
  } = props;
  const [error, setErrors] = useState('');
  const isMSPProduct = useProduct('MSP');
  const [siteList, setSiteList] = useState([]);
  const [loading, setLoading] = useState(false);
  const ALL_SITES = {
    id: 'ALL_SITES',
    name: getTranslatedText(intl, 'client.contract.invoiceItems.allSites'),
    label: getTranslatedText(intl, 'client.contract.invoiceItems.allSites'),
    value: null
  };
  useEffect(() => {
    setErrors(props.errors);
  }, [props.errors]);

  const { getClientSites, data: sites } = useSiteListForDropdown(
    undefined,
    undefined,
    true
  );

  useEffect(() => {
    if (props.isMSPClientProduct && props.accountId) {
      setLoading(true);
      getClientSites(props.accountId);
    }
  }, [props.isMSPClientProduct, props.accountId]);

  useEffect(() => {
    if (props.accountId && !prePopulateOptions && !disableApiCalls) {
      setLoading(true);
      getClientSites(props.accountId);
    }
  }, [props.accountId, disableApiCalls]);

  useEffect(() => {
    if (!isMSPProduct && !prePopulateOptions && !disableApiCalls) {
      setLoading(true);
      getClientSites();
    }
  }, [isMSPProduct, disableApiCalls]);

  useEffect(() => {
    if (sites || prePopulateOptions) {
      const siteListData = sites || prePopulateOptions;
      setLoading(false);
      setSiteList(siteListData);
      if (
        siteListData.length > 0 &&
        props.isMSPClientProduct &&
        !ignoreAutoPopulateFirstValue
      ) {
        props.updateSite(!isEmpty(props.site) ? props.site : siteListData[0]);
        return;
      }
      if (
        siteListData.length === 1 &&
        !ignoreAutoPopulateFirstValue &&
        !multiSelect
      ) {
        props.updateSite(siteListData[0]);
      }
    }
  }, [sites, prePopulateOptions?.[0]?.id]);

  const constructDropDownError = () => {
    if (errors) {
      return reactSelectInlineError(errors);
    }
    return null;
  };

  if (mode === 'view') {
    return (
      <ReadMode className="flex actr p10">
        {!hideIcon &&
          (icon ? (
            <div className="opacity5">
              <SVGIcon name="Site" />
            </div>
          ) : (
            ''
          ))}
        <span className="mr-10">{props?.site?.name}</span>
        <SVGIcon name="Edit" />
      </ReadMode>
    );
  }

  if (!props?.accountId && isMSPProduct) {
    return (
      <>
        <SelectWrapper
          fullWidth={props?.fullWidth}
          className={props?.fullWidth ? 'w100' : ''}
          noMargin={noMargin}
        >
          {!hideLabel && (
            <div className="Polaris-Labelled__LabelWrapper ml-10">
              <div className="Polaris-Label">
                <label
                  id="userPhoneLabel"
                  htmlFor="userPhone"
                  className="Polaris-Label__Text"
                >
                  <FormattedMessage id="ticket.newProject.details.site" />
                </label>
              </div>
            </div>
          )}
          <div className="flex center">
            {!hideIcon &&
              (icon ? (
                <Badge
                  status={roundedIcon ? 'message' : 'none'}
                  radial={roundedIcon}
                  size="small"
                >
                  <div className="opacity5 flex">
                    <SVGIcon name="Site" />
                  </div>
                </Badge>
              ) : (
                ''
              ))}
            <div className="container w100">
              <Select
                components={{ IndicatorSeparator, DropdownArrow }}
                classNamePrefix="value_selector"
                placeholder={getTranslatedText(
                  intl,
                  'ticket.newProject.details.site.pl'
                )}
                styles={
                  props?.errors
                    ? {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({
                          ...base,
                          ...menuStyleProps,
                          zIndex: 9999
                        }),
                        menuList: (base) => ({
                          ...base,
                          ...menuListStyleProps
                        }),
                        ...reactSelectErrorStyles
                      }
                    : {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menuList: (base) => ({
                          ...base,
                          ...menuListStyleProps
                        }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }
                }
                openMenuOnFocus={defaultMenuOpen}
                isDisabled={disabled}
                isMulti={multiSelect}
              />
              {constructDropDownError()}
            </div>
          </div>
        </SelectWrapper>
      </>
    );
  }

  let siteData = siteList.map((el) => {
    return {
      ...el,
      value: el.name,
      label: el.label || el.name
    };
  });

  // If showAllSitesOption is enabled, have all sites option selected by default.
  if (selectAllSitesOption) {
    siteData = [ALL_SITES].concat(siteData);
  }
  let displayValue = null;
  if (multiSelect) {
    displayValue = props?.site
      ? props?.site?.map((el) => {
          return { ...el, ...siteData.find((site) => site.id === el.id) };
        })
      : [];
  } else if (props?.site?.id) {
    displayValue = siteData.find((el) => el.id === props?.site?.id);
  }
  if (!displayValue && selectAllSitesOption) {
    displayValue = ALL_SITES;
  }

  return (
    <SelectWrapper
      fullWidth={props?.fullWidth}
      className="site-custom-class"
      noMargin={noMargin}
    >
      {!hideLabel && (
        <div className="Polaris-Labelled__LabelWrapper ml-10">
          <div className="Polaris-Label">
            <label
              id="userPhoneLabel"
              htmlFor="userPhone"
              className="Polaris-Label__Text"
            >
              <FormattedMessage id="ticket.newProject.details.site" />
            </label>
          </div>
        </div>
      )}
      <div className="flex actr g5">
        {!hideIcon &&
          (icon ? (
            <div className="opacity5">
              <Badge
                status={roundedIcon ? 'message' : 'none'}
                radial={roundedIcon}
                size="small"
              >
                <SVGIcon name="Site" />
              </Badge>
            </div>
          ) : (
            ''
          ))}

        <div className="container">
          <Select
            key={multiSelect ? JSON.stringify(displayValue) : displayValue?.id}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            value={displayValue}
            components={{
              IndicatorSeparator,
              DropdownArrow
            }}
            title=""
            onChange={(val) => {
              if (multiSelect) {
                props.updateSite(
                  val?.map((el) => ({
                    ...el,
                    id: el.id,
                    name: el.name,
                    value: el.name
                  })) || []
                );
              } else {
                props.updateSite(val, siteList.length > 1 ? 'resetUser' : null);
              }
            }}
            options={siteData}
            placeholder={getTranslatedText(
              intl,
              'ticket.newProject.details.site.pl'
            )}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            styles={
              props?.errors
                ? {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({
                      ...base,
                      ...menuStyleProps,
                      zIndex: 9999
                    }),
                    menuList: (base) => ({
                      ...base,
                      ...menuListStyleProps
                    }),
                    ...reactSelectErrorStyles
                  }
                : {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menuList: (base) => ({
                      ...base,
                      ...menuListStyleProps
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 })
                  }
            }
            openMenuOnFocus={defaultMenuOpen}
            autoFocus={defaultMenuOpen}
            name="valueselector"
            isLoading={loading}
            isDisabled={disabled}
            isMulti={multiSelect}
            isClearable={!disableClearable}
          />
          {constructDropDownError()}
        </div>
      </div>
    </SelectWrapper>
  );
};

export default injectIntl(SiteSelect);
